<template>
  <div class="simulation-recommendation-selection">
    <v-btn
      v-if="!isLoading"
      @click="fetchRecommendations"
      :disabled="isLoading"
      id="generate-recommendations-btn"
    >
      Generate Recommendations
    </v-btn>
    <span v-else class="loading-indicator">
      <v-icon>mdi-loading mdi-spin</v-icon>
      <span>Loading...</span>
    </span>

    <div
      v-if="hasRecommendations || isLoading"
      class="recommendations-container"
    >
      <!-- Summary Section -->
      <div v-if="isLoading" class="summary-section mb-6">
        <h3>Summary</h3>
        <v-skeleton-loader type="paragraph" :loading="true"></v-skeleton-loader>
      </div>
      <div v-else-if="summary" class="summary-section mb-6">
        <h3>Summary</h3>
        <p class="multiline-text">{{ summary }}</p>
      </div>

      <!-- ProBono Tools Section -->
      <div v-if="isLoading">
        <h3 class="section-title mb-4">Recommended Tools</h3>
        <div class="tools">
          <v-card
            v-for="i in 6"
            :key="`probono-skeleton-${i}`"
            class="tool-card-wrapper"
          >
            <v-skeleton-loader
              type="heading, text, text"
              :loading="true"
            ></v-skeleton-loader>
          </v-card>
        </div>
      </div>
      <div v-else-if="sortedProbonoTools.length">
        <h3 class="section-title mb-4">Recommended Tools</h3>
        <div class="tools">
          <div
            v-for="(tool, index) in sortedProbonoTools"
            :key="`probono-${index}`"
            class="tool-card-wrapper"
          >
            <tool-card :tool="tool" />
          </div>
        </div>
      </div>

      <!-- Other Tools Section -->
      <div v-if="isLoading">
        <h3 class="section-title mb-4">Other Tools</h3>
        <div class="tools">
          <v-card
            v-for="i in 3"
            :key="`other-skeleton-${i}`"
            class="tool-card-wrapper"
          >
            <v-skeleton-loader
              type="heading, text, text"
              :loading="true"
            ></v-skeleton-loader>
          </v-card>
        </div>
      </div>
      <div v-else-if="sortedOtherTools.length">
        <h3 class="section-title mb-4">Other Tools</h3>
        <div class="tools">
          <div
            v-for="(tool, index) in sortedOtherTools"
            :key="`other-${index}`"
            class="tool-card-wrapper"
          >
            <tool-card :tool="tool" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import apiClient from "@/plugins/apiService";
import ToolCard from "./ToolCard.vue";

export default {
  components: {
    ToolCard,
  },
  props: ["selectedLab", "selectedProblem"],
  data() {
    return {
      otherTools: [],
      probonoTools: [],
      summary: "",
      isLoading: false,
    };
  },
  computed: {
    hasRecommendations() {
      return (
        this.otherTools.length > 0 ||
        this.probonoTools.length > 0 ||
        this.summary
      );
    },
    sortedProbonoTools() {
      return [...this.probonoTools].sort((a, b) => {
        return (b.score || 0) - (a.score || 0);
      });
    },
    sortedOtherTools() {
      return [...this.otherTools].sort((a, b) => {
        return (b.score || 0) - (a.score || 0);
      });
    },
  },
  methods: {
    async fetchRecommendations() {
      this.isLoading = true;
      try {
        const response = await apiClient.post(`/gpt-recommendation`, {
          selectedProblem: this.selectedProblem,
          selectedLab: this.selectedLab,
        });
        if (response.status === 200) {
          const data = response.data;
          this.otherTools = data.other_tools || [];
          this.probonoTools = data.probono_tools || [];
          this.summary = data.summary_of_comments_of_all_tools || "N/A";
        } else {
          console.error("Error fetching recommendations:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching recommendations:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style scoped>
.simulation-recommendation-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
}

#generate-recommendations-btn {
  background: linear-gradient(
    135deg,
    var(--primaryColorDark) 0%,
    var(--darkColor) 100%
  ) !important;
  color: white;
  font-weight: bold;
  padding: 30px 30px;
  display: flex;
  align-items: center;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primaryColor);
  font-weight: bold;
}

.recommendations-container {
  margin-top: 24px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.summary-section {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid var(--primaryColor);
}

.summary-section h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--primaryColor);
  font-weight: bold;
}

.section-title {
  color: var(--primaryColor);
  font-weight: bold;
  font-size: 1.3rem;
  margin: 16px;
}

.tools {
  display: flex;
  gap: 24px;
  justify-content: flex-start;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 20px;
}

.multiline-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: visible;
  text-overflow: unset;
}
</style>
